<script setup lang="ts">
import type { ElementClass } from "@dsk/types";
import { computed } from "vue";

interface Props {
  modelValue?: string;
  label?: string;
  class?: ElementClass;
  hasError?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  label: undefined,
  class: undefined,
  hasError: false,
  disabled: false
});

const emit = defineEmits<{
  (event: "update:modelValue", value: string): void;
}>();

defineOptions({ inheritAttrs: false });

const COMPONENT_CLASS = "le-input";

const groupClasses = computed<string[]>(() => {
  const classes = [`${COMPONENT_CLASS}-group`];

  if (props.hasError) {
    classes.push(`${COMPONENT_CLASS}-group--has-error`);
  }

  if (props.disabled) {
    classes.push(`${COMPONENT_CLASS}-group--disabled`);
  }

  classes.push(props.class?.toString() || "");

  return classes;
});

const componentClasses = computed<ElementClass>(() => [
  COMPONENT_CLASS,
  {
    [`${COMPONENT_CLASS}--has-error`]: props.hasError
  }
]);
</script>

<template>
  <Component :is="label ? 'label' : 'div'" :class="`${COMPONENT_CLASS}-container`">
    <div v-if="label" :class="`${COMPONENT_CLASS}-label`">{{ label }}</div>
    <div :class="groupClasses">
      <div v-if="$slots.iconBefore" :class="`${COMPONENT_CLASS}-icon-before`">
        <slot name="iconBefore"></slot>
      </div>
      <input
        v-bind="$attrs"
        :disabled="disabled"
        :class="componentClasses"
        :value="modelValue"
        @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
      />
      <div v-if="$slots.iconAfter" :class="`${COMPONENT_CLASS}-icon-after`">
        <slot name="iconAfter"></slot>
      </div>
    </div>
  </Component>
</template>

<style scoped lang="less">
@import "@dsk/styles/all.less";

.le-input-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 0;
}

.le-input-label {
  margin-bottom: 2px;
  .body-text("xs");
  color: @colors[@neutrals][200];
  font-style: normal;
}

.le-input-group {
  display: flex;
  position: relative;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  transition: all 100ms ease-in-out;
  border: 1px solid @colors[@secondary][600];
  border-radius: 4px;
  background: @colors[@neutrals][100];

  &:focus-within {
    border-color: @colors[@primary][600];
    .focus-outline(green);
  }

  &--disabled {
    opacity: 0.4;
  }

  &--has-error {
    border-color: @colors[@error][600];

    &:focus-within {
      border-color: @colors[@error][600];
      .focus-outline(red);
    }
  }
}

.le-input-icon-before,
.le-input-icon-after {
  display: flex;
  align-items: center;
  padding: 0;
}

.le-input-icon-before {
  margin-right: 6px;
}

.le-input-icon-after {
  margin-left: 6px;
}

.le-input {
  display: flex;
  flex: 1 1 auto;
  width: 1%; // Only fill as much as is needed
  padding: 0;
  border: none;
  outline: none;
  background: none;
  .body-text("m");
  color: @colors[@neutrals][500];

  &::placeholder {
    opacity: 0.4;
    color: @colors[@neutrals][500];
  }
}
</style>
